package com.example.hukakmp.model

import dev.gitlive.firebase.firestore.Timestamp
import kotlinx.serialization.Serializable

enum class TournamentParticipantStatus {
    PENDIENTE,
    ACEPTADO,
    RECHAZADO,
}

enum class TournamentScoreType(val displayName: String) {
    CANTIDAD_PECES("Cantidad de peces"),
    ESPECIES_DISTINTAS("Especies distintas"),
    PERSONALIZADO("Reglas personalizadas"),
}

enum class TournamentStatus {
    PROXIMO,
    ACTIVO,
    FINALIZADO,
}

enum class TournamentReportStatus {
    ACTIVO,
    RECHAZADO,
}

@Serializable
data class TournamentScoringRules(
    val bonusPrimerParte: Int? = null,
    val puntosPorPez: Int? = null,
    val puntosPorEspecie: Map<String, Int>? = null,
    val puntosOtrosPeces: Int = 0,
) {
    val hasAnyRule: Boolean
        get() = bonusPrimerParte != null ||
            puntosPorPez != null ||
            !puntosPorEspecie.isNullOrEmpty()
}

@Serializable
data class Tournament(
    val id: String = "",
    val nombre: String = "",
    val descripcion: String = "",
    val creatorId: String = "",
    val creatorName: String = "",
    val fechaInicio: Timestamp = Timestamp(0, 0),
    val fechaFin: Timestamp = Timestamp(0, 0),
    val tipoPuntaje: String = TournamentScoreType.CANTIDAD_PECES.name,
    val reglasPersonalizadas: String = "",
    val reglas: TournamentScoringRules? = null,
    val codigoInvitacion: String = "",
    val creadoEn: Timestamp = Timestamp(0, 0),
)

@Serializable
data class TournamentSpecies(
    val nombre: String = "",
    val cantidad: Int = 0,
)

@Serializable
data class TournamentReport(
    val parteId: String = "",
    val userId: String = "",
    val userName: String = "",
    val fecha: String = "",
    val especies: List<TournamentSpecies> = emptyList(),
    val fotos: List<String> = emptyList(),
    val puntaje: Int = 0,
    val estado: String = TournamentReportStatus.ACTIVO.name,
    val motivoRechazo: String = "",
    val creadoEn: Timestamp = Timestamp(0, 0),
)

@Serializable
data class TournamentParticipant(
    val userId: String = "",
    val userName: String = "",
    val userPhoto: String = "",
    val estado: String = TournamentParticipantStatus.PENDIENTE.name,
    val puntaje: Int = 0,
    val parteIds: List<String> = emptyList(),
    val solicitadoEn: Timestamp = Timestamp(0, 0),
    val respondidoEn: Timestamp? = null,
)

data class TournamentSummary(
    val tournament: Tournament,
    val participants: List<TournamentParticipant> = emptyList(),
    val reports: List<TournamentReport> = emptyList(),
    val isCreator: Boolean = false,
    val myStatus: TournamentParticipantStatus? = null,
    val myScore: Int = 0,
    val myPosition: Int = 0,
) {
    val pending: List<TournamentParticipant>
        get() = participants.filter { it.statusEnum() == TournamentParticipantStatus.PENDIENTE }

    val accepted: List<TournamentParticipant>
        get() = participants
            .filter { it.statusEnum() == TournamentParticipantStatus.ACEPTADO }
            .sortedByDescending { it.puntaje }

    val activeReports: List<TournamentReport>
        get() = reports
            .filter { it.statusEnum() == TournamentReportStatus.ACTIVO }
            .sortedByDescending { it.creadoEn.seconds }

    val rejectedReports: List<TournamentReport>
        get() = reports
            .filter { it.statusEnum() == TournamentReportStatus.RECHAZADO }
            .sortedByDescending { it.creadoEn.seconds }
}

fun TournamentReport.statusEnum(): TournamentReportStatus =
    TournamentReportStatus.entries.firstOrNull { it.name == estado }
        ?: TournamentReportStatus.ACTIVO

fun TournamentParticipant.statusEnum(): TournamentParticipantStatus =
    TournamentParticipantStatus.entries.firstOrNull { it.name == estado }
        ?: TournamentParticipantStatus.PENDIENTE

fun Tournament.scoreTypeEnum(): TournamentScoreType =
    TournamentScoreType.entries.firstOrNull { it.name == tipoPuntaje }
        ?: TournamentScoreType.CANTIDAD_PECES

fun Tournament.status(now: Timestamp = Timestamp.now()): TournamentStatus = when {
    now.seconds < fechaInicio.seconds -> TournamentStatus.PROXIMO
    now.seconds > fechaFin.seconds -> TournamentStatus.FINALIZADO
    else -> TournamentStatus.ACTIVO
}
